/**
 * Frontend analytics factory functions.
 *
 * Each factory builds the typed tracking surface for one product area.
 * They live apart from the public hooks (useAnalytics, getEmailDomain,
 * createTimingTracker) so the service module stays small and focused.
 */

import { usePostHog } from 'posthog-js/react';

// ============================================================================
// Event Property Types
// ============================================================================

interface BaseEventProperties {
  sessionId?: string;
  reviewId?: string;
  timestamp?: string;
}

interface PageViewProperties extends BaseEventProperties {
  path: string;
  referrer?: string;
}

interface FileUploadProperties extends BaseEventProperties {
  fileCount: number;
  totalSize: number;
  fileTypes: string[];
  uploadDuration?: number;
}

interface FileValidationProperties extends BaseEventProperties {
  fileCount: number;
  validationResult: 'success' | 'error';
  errorMessage?: string;
}

interface EmailSubmitProperties extends BaseEventProperties {
  emailDomain?: string;
  hasAcceptedTerms: boolean;
}

interface AnalysisStartProperties extends BaseEventProperties {
  analysisType: 'quick' | 'full';
  fileCount: number;
  totalWordCount?: number;
}

interface AnalysisCompleteProperties extends BaseEventProperties {
  analysisType: 'quick' | 'full';
  duration: number;
  status: 'completed' | 'failed' | 'cancelled';
  errorMessage?: string;
}

interface QuickAnalysisViewProperties extends BaseEventProperties {
  contractType?: string;
  issueCount?: number;
}

interface PaymentInitiatedProperties extends BaseEventProperties {
  amount?: number;
  wordCount?: number;
}

interface ResultsViewProperties extends BaseEventProperties {
  issueCount: number;
  highRiskIssueCount: number;
  contractType?: string;
  wordCount?: number;
  clauseCount?: number;
}

interface IssueExpandedProperties extends BaseEventProperties {
  issueId: string;
  issuePriority: number;
  articleNumber?: string;
}

interface WhatsAppCopyProperties extends BaseEventProperties {
  templateType: 'summary' | 'risk_alert' | 'consultation';
}

interface ShareResultsProperties extends BaseEventProperties {
  shareMethod: 'link' | 'whatsapp' | 'email';
}

interface SurveyAnsweredProperties extends BaseEventProperties {
  questionIndex: number;
  question: string;
  answer: string;
}

interface SessionCreatedProperties extends BaseEventProperties {
  source?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

interface ErrorProperties extends BaseEventProperties {
  errorType: string;
  errorMessage: string;
  errorStack?: string;
  context?: string;
  statusCode?: number;
}

interface EarlyAccessAnalytics {
  pageOpened: (properties?: EarlyAccessPageOpenProperties) => void;
  fileUploaded: (properties: EarlyAccessFileUploadProperties) => void;
  introOpened: () => void;
  submitClicked: () => void;
  submitSucceeded: (properties: EarlyAccessSubmitProperties) => void;
  submitFailed: (properties: { message: string }) => void;
}

interface EarlyAccessSubmitProperties {
  priceCaptured: number;
  followStatus: 'already_follow' | 'not_follow';
  contactMethod: 'whatsapp' | 'email';
}

interface EarlyAccessFileUploadProperties {
  fileName: string;
  fileSize: number;
  fileType: string;
}

interface EarlyAccessPageOpenProperties {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface JudgementSummarizationAnalytics {
  startClicked: () => void;
}

interface RegulationSummaryTabViewProperties {
  regulationId: string;
  isLoggedIn: boolean;
}

interface RegulationSummaryLoginCTAProperties {
  regulationId: string;
}

interface RegulationSummaryRequestProperties {
  regulationId: string;
}

interface RegulationSummarizationAnalytics {
  startClicked: () => void;
  tabViewed: (properties: RegulationSummaryTabViewProperties) => void;
  loginCTAClicked: (properties: RegulationSummaryLoginCTAProperties) => void;
  requestClicked: (properties: RegulationSummaryRequestProperties) => void;
}

interface FeaturesAnalytics {
  pageViewed: () => void;
  trialCTAClicked: () => void;
  featureCardClicked: (properties: { featureId: string; featureName: string }) => void;
}


// ============================================================================
// Analytics Service
// ============================================================================

/**
 * Periksa Kontrak specific analytics events
 * Organized by user journey: Upload → Preview → Payment → Results
 */
export interface PeriksaKontrakAnalytics {
  // Upload Page Events
  pageViewed: (properties?: Partial<PageViewProperties>) => void;
  sessionCreated: (properties: SessionCreatedProperties) => void;
  sessionIdCopied: (properties?: BaseEventProperties) => void;
  filesSelected: (properties: { fileCount: number; sessionId?: string }) => void;
  fileValidationStarted: (properties: FileValidationProperties) => void;
  fileValidationCompleted: (properties: FileValidationProperties) => void;
  emailEntered: (properties: EmailSubmitProperties) => void;
  termsAccepted: (properties?: BaseEventProperties) => void;
  fileUploadStarted: (properties: FileUploadProperties) => void;
  fileUploadCompleted: (properties: FileUploadProperties) => void;
  fileUploadFailed: (properties: FileUploadProperties & { errorMessage: string }) => void;
  fileRemoved: (properties: { fileName: string; sessionId?: string }) => void;
  startConfirmationOpened: (properties?: BaseEventProperties) => void;
  startConfirmationCancelled: (properties?: BaseEventProperties) => void;

  // Preview/Pratinjau Page Events
  previewPageViewed: (properties?: BaseEventProperties) => void;
  quickAnalysisStarted: (properties: AnalysisStartProperties) => void;
  quickAnalysisPollingStarted: (properties?: BaseEventProperties) => void;
  quickAnalysisCompleted: (properties: AnalysisCompleteProperties) => void;
  quickAnalysisViewed: (properties: QuickAnalysisViewProperties) => void;
  paymentButtonClicked: (properties: PaymentInitiatedProperties) => void;
  fullAnalysisStarted: (properties: AnalysisStartProperties) => void;

  // Results/Hasil Page Events
  resultsPageViewed: (properties?: BaseEventProperties) => void;
  resultsPollingStarted: (properties?: BaseEventProperties) => void;
  resultsLoaded: (properties: ResultsViewProperties) => void;
  issueExpanded: (properties: IssueExpandedProperties) => void;
  issueCollapsed: (properties: IssueExpandedProperties) => void;
  whatsappTemplateCopied: (properties: WhatsAppCopyProperties) => void;
  resultsShared: (properties: ShareResultsProperties) => void;
  newAnalysisStarted: (properties?: BaseEventProperties) => void;
  surveyAnswered?: (properties: SurveyAnsweredProperties) => void;

  // Error Events
  errorOccurred: (properties: ErrorProperties) => void;
}

/**
 * Creates the analytics service interface
 * Internal implementation hidden - provides clean API surface
 */
export function createPeriksaKontrakAnalytics(posthog: ReturnType<typeof usePostHog>): PeriksaKontrakAnalytics {
  const track = (eventName: string, properties?: Record<string, any>) => {
    if (!posthog) {
      console.warn('[Analytics] PostHog not initialized');
      return;
    }

    // Add common metadata
    const enrichedProperties = {
      ...properties,
      timestamp: properties?.timestamp || new Date().toISOString(),
      product: 'periksa_kontrak',
    };

    posthog.capture(eventName, enrichedProperties);
  };

  return {
    // Upload Page Events
    pageViewed: (properties) => track('periksa_kontrak_page_viewed', properties),
    sessionCreated: (properties) => track('periksa_kontrak_session_created', properties),
    sessionIdCopied: (properties) => track('periksa_kontrak_session_id_copied', properties),
    filesSelected: (properties) => track('periksa_kontrak_files_selected', properties),
    fileValidationStarted: (properties) => track('periksa_kontrak_file_validation_started', properties),
    fileValidationCompleted: (properties) => track('periksa_kontrak_file_validation_completed', properties),
    emailEntered: (properties) => track('periksa_kontrak_email_entered', properties),
    termsAccepted: (properties) => track('periksa_kontrak_terms_accepted', properties),
    fileUploadStarted: (properties) => track('periksa_kontrak_file_upload_started', properties),
    fileUploadCompleted: (properties) => track('periksa_kontrak_file_upload_completed', properties),
    fileUploadFailed: (properties) => track('periksa_kontrak_file_upload_failed', properties),
    fileRemoved: (properties) => track('periksa_kontrak_file_removed', properties),
    startConfirmationOpened: (properties) => track('periksa_kontrak_start_confirmation_opened', properties),
    startConfirmationCancelled: (properties) => track('periksa_kontrak_start_confirmation_cancelled', properties),

    // Preview/Pratinjau Page Events
    previewPageViewed: (properties) => track('periksa_kontrak_preview_page_viewed', properties),
    quickAnalysisStarted: (properties) => track('periksa_kontrak_quick_analysis_started', properties),
    quickAnalysisPollingStarted: (properties) => track('periksa_kontrak_quick_analysis_polling_started', properties),
    quickAnalysisCompleted: (properties) => track('periksa_kontrak_quick_analysis_completed', properties),
    quickAnalysisViewed: (properties) => track('periksa_kontrak_quick_analysis_viewed', properties),
    paymentButtonClicked: (properties) => track('periksa_kontrak_payment_button_clicked', properties),
    fullAnalysisStarted: (properties) => track('periksa_kontrak_full_analysis_started', properties),

    // Results/Hasil Page Events
    resultsPageViewed: (properties) => track('periksa_kontrak_results_page_viewed', properties),
    resultsPollingStarted: (properties) => track('periksa_kontrak_results_polling_started', properties),
    resultsLoaded: (properties) => track('periksa_kontrak_results_loaded', properties),
    issueExpanded: (properties) => track('periksa_kontrak_issue_expanded', properties),
    issueCollapsed: (properties) => track('periksa_kontrak_issue_collapsed', properties),
    whatsappTemplateCopied: (properties) => track('periksa_kontrak_whatsapp_template_copied', properties),
    resultsShared: (properties) => track('periksa_kontrak_results_shared', properties),
    newAnalysisStarted: (properties) => track('periksa_kontrak_new_analysis_started', properties),
    surveyAnswered: (properties) => track('periksa_kontrak_survey_answered', properties),

    // Error Events
    errorOccurred: (properties) => track('periksa_kontrak_error', properties),
  };
}

export function createEarlyAccessAnalytics(posthog: ReturnType<typeof usePostHog>): EarlyAccessAnalytics {
  const track = (eventName: string, properties?: Record<string, any>) => {
    if (!posthog) {
      console.warn('[Analytics] PostHog not initialized');
      return;
    }

    const enrichedProperties = {
      ...properties,
      timestamp: properties?.timestamp || new Date().toISOString(),
      product: 'early_access_form',
    };

    posthog.capture(eventName, enrichedProperties);
  };

  return {
    pageOpened: (properties) => track('contract_review_early_access_page_open', properties),
    fileUploaded: (properties) => track('contract_review_early_access_file_upload', properties),
    introOpened: () => track('contract_review_early_access_intro_opened'),
    submitClicked: () => track('contract_review_early_access_submit_clicked'),
    submitSucceeded: (properties) => track('contract_review_early_access_submit_success', properties),
    submitFailed: (properties) => track('contract_review_early_access_submit_error', properties),
  };
}

export function createJudgementSummarizationAnalytics(posthog: ReturnType<typeof usePostHog>): JudgementSummarizationAnalytics {
    const track = (eventName: string, properties?: Record<string, any>) => {
        if (!posthog) {
            console.warn('[Analytics] PostHog not initialized');
            return;
        }

        const enrichedProperties = {
            ...properties,
            timestamp: properties?.timestamp || new Date().toISOString(),
            product: 'judgement_summarization',
        };

        posthog.capture(eventName, enrichedProperties);
    };

    return {
        startClicked: () => track('judgement_summarization_start_clicked'),
    };
}

export function createRegulationSummarizationAnalytics(posthog: ReturnType<typeof usePostHog>): RegulationSummarizationAnalytics {
    const track = (eventName: string, properties?: Record<string, any>) => {
        if (!posthog) {
            console.warn('[Analytics] PostHog not initialized');
            return;
        }

        const enrichedProperties = {
            ...properties,
            timestamp: properties?.timestamp || new Date().toISOString(),
            product: 'regulation_summarization',
        };

        posthog.capture(eventName, enrichedProperties);
    };

    return {
        startClicked: () => track('regulation_summarization_start_clicked'),
        tabViewed: (properties) => track('regulation_summary_tab_viewed', properties),
        loginCTAClicked: (properties) => track('regulation_summary_login_cta_clicked', properties),
        requestClicked: (properties) => track('regulation_summary_request_clicked', properties),
    };
}

export function createFeaturesAnalytics(posthog: ReturnType<typeof usePostHog>): FeaturesAnalytics {
  const track = (eventName: string, properties?: Record<string, any>) => {
    if (!posthog) {
      console.warn('[Analytics] PostHog not initialized');
      return;
    }

    const enrichedProperties = {
      ...properties,
      timestamp: properties?.timestamp || new Date().toISOString(),
      product: 'features_page',
    };

    posthog.capture(eventName, enrichedProperties);
  };

  return {
    pageViewed: () => track('features_page_viewed'),
    trialCTAClicked: () => track('features_trial_cta_clicked'),
    featureCardClicked: (properties) => track('features_card_clicked', properties),
  };
}
